/* global fbq, klaviyo, Sentry */
/**
 *
 *  Checkout Page
 *  Checkout Component
 *
 *  Created by Vladimir Shishlyannikov on 06/03/2023.
 *  Copyright (c) 2023-present iWoo Ltd. All rights reserved.
 *
 */

import React, { useCallback, useEffect, useRef, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import Modal from "react-modal";
import { useStripe } from "@stripe/react-stripe-js";
import * as Sentry from "@sentry/browser";
import { Title, Meta } from "react-meta-elements";

import useDebouncedCallback from "../../hooks/useDebouncedCallback/useDebouncedCallback";

import { useAppState } from "../../state";
import { useSignUpState } from "../../state/SignUpState";
import { useRegion } from "../../context/RegionProvider";

// Components
import Button from "../Button/Button";
import Header from "../Header/Header";
import Footer from "../BecomeMemberPage/Footer/Footer";
import PartnerProductBadge from "../PartnerProductBadge/PartnerProductBadge";
import ScrollToTopOnMount from "../ScrollToTopOnMount/ScrollToTopOnMount";
import PromoCodeInput from "./PromoCodeInput/PromoCodeInput";
import TrainerRating from "../TrainerRating/TrainerRating";
import UserContactsBox from "./UserContactsBox/UserContactsBox";
import UserInfoModal from "../UserInfoModal/UserInfoModal";
import Registration from "../SignUp/Registration/Registration";

// Hooks
import useTheme from "../ThemeProvider/useTheme/useTheme";
import usePrice from "./usePrice/usePrice";
import useLatest from "../../hooks/useLatest/useLatest";

// Icons
import MapMarkerIcon from "../../icons/MapMarkerIcon";
import CalendarTruBeIcon from "../../icons/CalendarTruBeIcon";
import ArrowLeftIcon from "../../icons/ArrowLeftIcon";
import PenIcon from "../../icons/PenIcon";
import ExclamationMarkOIcon from "../../icons/ExclamationMarkOIcon";
import CloseIcon from "../../icons/CloseIcon";
import BlockerIcon from "../../icons/BlockerIcon";
import CheckNiceIcon from "../../icons/CheckNiceIcon";
import SessionTypeIcon from "../UserProfilePage/SessionTypeIcon";

// Utils
import { getMomentWithTz, getTz } from "../../utils/date";
import {
  clientWidth,
  getPartnerPictureUrl,
  isValidFirstName,
  isValidLastName,
} from "../../utils";
import { FB_EVENT_PURCHASE, modalStyles } from "../../constants";

import { formatEventDates } from "../EventPage/EventPage";

// Images
import GenericUserProfilePhoto from "../ProfilePicture/DefaultProfilePic";

import defaultPartnerPhoto from "../../images/generic-trainerphoto.svg";

import BestSeller from "./BestSeller";
import LotusBig from "./LotusBig";
import TimeZoneLabel from "../TimeZoneLabel/TimeZoneLabel";

const BookingOption = ({
  id,
  disabled,
  checked,
  title,
  featuredText,
  save,
  discount,
  price,
  currencySymbol,
  value,
  sessions,
  onChange,
}) => {
  const handleChange = useCallback(() => {
    onChange && onChange(value);
  }, [value, onChange]);

  return (
    <div className="tbk-relative">
      {sessions === 15 ? (
        <BestSeller
          className=""
          style={{
            content: "",
            position: "absolute",
            width: "62px",
            height: "62px",
            top: "-19px",
            right: "-18px",
          }}
        />
      ) : null}
      <input
        type="radio"
        id={`bookingOption_${id}`}
        checked={checked}
        disabled={disabled}
        onChange={handleChange}
      />
      <label className="tbk-flex tbk-pl-5" htmlFor={`bookingOption_${id}`}>
        <div className=" tbk-grow">
          <div className="tbk-text-main tbk-mb-1 tbk-text-primary">{title}</div>
          <div className="tbk-text-small tbk-text-green">
            {save && discount
              ? `Save ${save}% / Discount ${currencySymbol}${discount}`
              : null}
            <span className="tbk-text-blue-grey">
              {save && discount ? " / " : null}
              {featuredText}
            </span>
          </div>
        </div>
        <div className="tbk-text-main tbk-w-7 tbk-text-grey-main">
          {currencySymbol}
          {price}
        </div>
      </label>
    </div>
  );
};

const BookingOptionPlaceholder = ({ id, disabled }) => {
  return (
    <div className="tbk-relative">
      <input
        type="radio"
        // id={`bookingOption_${id}`}
        checked={false}
        disabled={disabled}
      />
      <label
        className="tbk-flex tbk-pl-5"
        // htmlFor={`bookingOption_${id}`}
      >
        <div className=" tbk-grow">
          <div className="tbk-mb-1 tbk-mr-4 tbk-h-[26px] tbk-rounded-lg tbk-bg-grey-light"></div>
          <div className="tbk-h-[18px] tbk-w-2/3 tbk-rounded-lg tbk-bg-grey-light">
            <span className="tbk-text-blue-grey"></span>
          </div>
        </div>
        <div className="tbk-h-[26px] tbk-w-7 tbk-rounded-lg tbk-bg-grey-light"></div>
      </label>
    </div>
  );
};

const PriceShimmer = () => (
  <div className={"tbk-relative tbk-overflow-hidden"}>
    <div
      className={"tbk-h-4 tbk-w-9 tbk-rounded-lg tbk-bg-grey-light tbk-pt-0.5"}
    />
    <div
      className={
        "tbk-absolute tbk-top-0 tbk-left-0 tbk-right-0 tbk-bottom-0 tbk-animate-placeholder-shimmer tbk-bg-coach-card-placeholder"
      }
    />
  </div>
);

const Checkout = () => {
  const history = useHistory();
  const location = useLocation();
  const { theme } = useTheme();
  const { region } = useRegion();
  const stripe = useStripe();

  const {
    user,
    products,
    partnerSummary,
    eventSummary,
    prices: partnerPrices,
    discount: discountPromoCode,
    referral,
    promoCode: userPromoCode,
    referralCode,
    referralValidation,
    confirmedBooking,
    bookingItems,
    membershipItems,
    previewCheckoutSession,
    customerCart,
    customerCartLoaded,
    clearDiscount,
    clearPrices,
    clearCustomerActivePackage,
    clearCustomerCart,
    getBookingItems,
    getMembershipItems,
    getCustomerCart,
    createCustomerCart,
    deleteCustomerCart,
    addProductToCart,
    removeProductsFromCart,
    createCheckoutSession,
    updateCustomerInfo,
    cartItems,
    activePackage: customerActivePackage,
    customerMembership,
    customerMembershipLoaded,
    // isTrialAvailable,
    isFetching,
  } = useAppState();

  const isTrialAvailable = false;

  const { userFirstName, userFamilyName, setUserFirstName, setUserFamilyName } =
    useSignUpState();

  const defaultCustomStyle =
    clientWidth() < theme.responsive.breakpoints.sm
      ? Object.assign(
          {},
          { ...modalStyles.mobileDefault },
          {
            overlay: {
              ...modalStyles.mobileDefault.overlay,
              zIndex: 1060,
            },
          }
        )
      : Object.assign(
          {},
          { ...modalStyles.default },
          { overlay: { ...modalStyles.default.overlay, zIndex: 1060 } }
        );

  const [isInfoModalOpen, setIsInfoModalOpen] = useState(false);
  const [infoModalTitle, setInfoModalTitle] = useState("");
  const [infoModalMsg, setInfoModalMsg] = useState("");
  const [infoModalStatus, setInfoModalStatus] = useState("Success");

  const [addressDetails, setAddressDetails] = useState("");
  const [additionalNotes, setAdditionalNotes] = useState("");
  const [selectedBookingOption, setSelectedBookingOption] = useState(null);
  const [selectedPaymentOption, setSelectedPaymentOption] = useState(null);
  const [isNotesModalOpen, setIsNotesModalOpen] = useState(false);
  const [promoCode, setPromoCode] = useState(referralCode || userPromoCode);
  const [hasPromoCodeError, setHasPromoCodeError] = useState(false);
  const [isReferralApplied, setIsReferralApplied] = useState(false);
  const [isPaymentFetching, setIsPaymentFetching] = useState(false);
  const [isPromoCodeChecking, setIsPromoCodeChecking] = useState(false);
  const [isUserInfoModalOpen, setIsUserInfoModalOpen] = useState(false);

  const [product] = useState(
    products.find((val) => val.id === confirmedBooking.productId)
  );

  const infoModalResolveRef = useRef(null);
  const promoCodeInputRef = useRef(null);
  const paymentRequestRef = useRef(null); // start booking when AP is the default method
  const addProductToCartCancelablePromiseRef = useRef();
  const createCartCancelablePromiseRef = useRef();
  const deleteCartCancelablePromiseRef = useRef();
  const emptyCartCancelablePromiseRef = useRef();
  const isPaymentFetchingRef = useRef(false);
  const userInfoModalResolveRef = useRef(null);
  const userInfoModalRejectRef = useRef(null);

  const latestCustomerCart = useLatest(customerCart);
  const latestUser = useLatest(user);

  const [price] = usePrice(
    selectedBookingOption ? selectedBookingOption.price.amount : 0,
    discountPromoCode,
    referral ? referral : referralValidation,
    isReferralApplied
  );

  const [isMembershipPurchase, setIsMembershipPurchase] = useState(
    confirmedBooking.membershipId
  );
  const [isSingleTicketPurchase, setIsSingleTicketPurchase] = useState(
    confirmedBooking.eventId && !customerMembership
  );
  const isGatedEvent = !!confirmedBooking.tier;
  const isAdditionalEventPurchase = !!(
    confirmedBooking.eventId &&
    customerMembership &&
    !isGatedEvent
  );

  const firstName = userFirstName || user.firstName;
  const familyName = userFamilyName || user.lastName;

  useEffect(() => {
    if (!customerMembership || isGatedEvent) {
      return;
    }
    const hasEnoughtEvents =
      customerMembership.metadata.usedEvents <
      customerMembership.metadata.totalEvents;
    if (isAdditionalEventPurchase && hasEnoughtEvents) {
      history.push(`/events/${confirmedBooking.eventId}/?auto_buy=true`);
    }
  }, [customerMembership /*isAdditionalEventPurchase, confirmedBooking*/]);

  useEffect(() => {
    if (partnerPrices && partnerPrices.length) {
      // setSelectedBookingOption(partnerPrices[0]);

      // Only for production
      if (typeof klaviyo !== "undefined") {
        // eslint-disable-next-line no-undef
        klaviyo.push([
          "track",
          "Started Checkout",
          {
            $event_id: `started_checkout_${new Date().getTime()}_${user.email}`, // event name+timestamp+customer email/id
            $value: partnerPrices[0].price.value,
            ItemNames: [
              `${partnerSummary.firstName} ${partnerSummary.lastName}`,
              product.name,
            ],
            CheckoutURL: window.location.href,
            Categories: [product.name],
            Items: [
              {
                ProductID: partnerSummary.id,
                SKU: `${partnerSummary.firstName.toUpperCase()}${partnerSummary.lastName.toUpperCase()}`,
                ProductName: `${partnerSummary.firstName} ${partnerSummary.lastName}`,
                Quantity: 1,
                ItemPrice: partnerPrices[0].price.value,
                RowTotal: partnerPrices[0].price.value,
                ProductURL: `https://${window.location.hostname}/partners/${partnerSummary.id}`,
                ImageURL: getPartnerPictureUrl(partnerSummary),
                ProductCategories: [product.name],
              },
            ],
          },
        ]);
      }
    }
  }, [partnerPrices]);

  useEffect(() => {
    getBookingItems({
      partnerId: confirmedBooking.partnerId,
      region: user ? user.region : region /*process.env.REACT_APP_REGION*/,
    });
    getMembershipItems({
      partnerId: confirmedBooking.partnerId,
      region: user ? user.region : region /*process.env.REACT_APP_REGION*/,
    });
    // getMembershipAdditionalEventItems(confirmedBooking.partnerId);

    return () => {
      clearPrices();
      clearDiscount();
      clearCustomerActivePackage();
      clearCustomerCart();
      // clearCustomerMembershipLoaded();
    };
  }, []);

  useEffect(() => {
    if (!user || !customerCartLoaded) {
      return;
    }

    if (!customerCart) {
      createCart(user.id, 1);
    }
  }, [user, customerCartLoaded]); // Only rerender when we try to get the customer's cart

  useEffect(() => {
    // customerMembership should be loaded by now (in Booking HOC)
    if (customerMembership && isMembershipPurchase) {
      const stateFrom = location && location.state && location.state.from;

      history.push(
        stateFrom && !stateFrom.includes("/login") ? stateFrom : "/lifestyle"
      );
    }

    if (confirmedBooking.eventId && !customerMembership) {
      setIsSingleTicketPurchase(true);
    } else {
      setIsSingleTicketPurchase(false);
    }
  }, [location, customerMembership]);

  useEffect(() => {
    if (!user) {
      return;
    }

    getCustomerCart(user.id);
  }, [user]);

  const checkDiscount = useCallback(
    (value) => {
      // Debounce time has elapsed
      setIsPromoCodeChecking(false);

      createSession(true)
        .then((res) => {
          // if (res.data.totalDetails.amountDiscount > 0) {
          // Promo code was successful
          setHasPromoCodeError(false);
          // } else {
          // setHasPromoCodeError(true);
          // }
        })
        .catch(() => {
          setHasPromoCodeError(true);
        });
    },
    [confirmedBooking]
  );

  const checkDiscountDebounced = useDebouncedCallback(checkDiscount, 3000);

  const createCart = useCallback(async (userId, maxRetries) => {
    let tries = 0;

    for (; tries < maxRetries; tries++) {
      try {
        createCartCancelablePromiseRef.current &&
          createCartCancelablePromiseRef.current.cancel();

        const cancelablePromise = createCustomerCart(userId);

        createCartCancelablePromiseRef.current = cancelablePromise; // assume emptyCard returns a cancelable promise

        await cancelablePromise.promise;

        return; // success, exit the loop
      } catch (err) {
        if (err.isCanceled) {
          console.log(`Request canceled, retry #${tries + 1}/${maxRetries}`);
        } else {
          console.log(
            `Error creating a cart, retry #${tries + 1}/${maxRetries}: ${err}`
          );

          if (tries >= maxRetries - 1) {
            throw err; // re-throw the error if all retries failed
          }
        }

        // Wait 200ms before retrying
        await sleep(1000);
      } finally {
        createCartCancelablePromiseRef.current = null;
      }
    }

    // if all retries failed, return a rejected promise
    return Promise.reject(new Error(`All retries failed to empty the cart`));
  }, []);

  const deleteCart = useCallback(async (cartId, userId, maxRetries) => {
    let tries = 0;

    for (; tries < maxRetries; tries++) {
      try {
        deleteCartCancelablePromiseRef.current &&
          deleteCartCancelablePromiseRef.current.cancel();

        const cancelablePromise = deleteCustomerCart(cartId, userId);

        deleteCartCancelablePromiseRef.current = cancelablePromise; // assume emptyCard returns a cancelable promise

        await cancelablePromise.promise;

        return; // success, exit the loop
      } catch (err) {
        if (err.isCanceled) {
          console.log(`Request canceled, retry #${tries + 1}/${maxRetries}`);
        } else {
          console.log(
            `Error deleting the cart, retry #${tries + 1}/${maxRetries}: ${err}`
          );

          if (tries >= maxRetries - 1) {
            throw err; // re-throw the error if all retries failed
          }
        }

        // Wait 200ms before retrying
        await sleep(1000);
      } finally {
        deleteCartCancelablePromiseRef.current = null;
      }
    }

    // if all retries failed, return a rejected promise
    return Promise.reject(new Error(`All retries failed to empty the cart`));
  }, []);

  const resetCartAndAddItemThenCreateSession = useCallback((vals) => {
    setIsPaymentFetching(true);

    if (!latestCustomerCart.current) {
      console.log("Customer's cart is null!");
      return;
    }

    Promise.resolve()
      .then(() => {
        if (
          latestCustomerCart.current.item &&
          latestCustomerCart.current.item.length !== 0
        ) {
          return removeItemsFromCart(
            latestCustomerCart.current.id,
            latestUser.current && latestUser.current.id,
            3
          );
        }
      })
      // .then(() => Promise.all(vals.map(val => addItem(val, 2))))
      .then(() => {
        // Последовательно добавляем элементы с задержкой
        return vals.reduce((promise, val) => {
          return (
            val && promise.then(() => addItem(val, 2)).then(() => sleep(1000))
          );
        }, Promise.resolve());
      })
      .then(() => sleep(1000))
      .catch(() => {
        // Item already in the cart
      })
      .then(() => {
        return createSession(true);
      })
      .finally(() => setIsPaymentFetching(false));
  }, []);

  const debouncedResetCartAndAddItemThenCreateSession = useDebouncedCallback(
    resetCartAndAddItemThenCreateSession,
    1000
  );

  const handleBookingOptionChange = useCallback(
    (val) => {
      if (!user || !customerCart) {
        return;
      }

      setSelectedBookingOption(val);
      if (val.metadata.type === "EVENT_MEMBERSHIP") {
        setIsMembershipPurchase(true);
        setIsSingleTicketPurchase(false);
      } else if (val.metadata.type === "EVENT") {
        setIsMembershipPurchase(false);
        setIsSingleTicketPurchase(true);
      }

      debouncedResetCartAndAddItemThenCreateSession([val]);

      if (
        isSingleBooking(val) ||
        val.metadata.type === "EVENT_MEMBERSHIP" ||
        val.metadata.type === "EVENT"
      ) {
        setIsReferralApplied(false);
        setPromoCode("");
        clearDiscount();
      }
    },
    [user, customerCart]
  );

  useEffect(() => {
    isPaymentFetchingRef.current = isPaymentFetching;
  }, [isPaymentFetching]);

  useEffect(() => {
    if (isPaymentFetchingRef.current || !customerCart) {
      return;
    }

    if (
      !(
        isMembershipPurchase ||
        isAdditionalEventPurchase ||
        isGatedEvent ||
        isSingleTicketPurchase
      ) &&
      bookingItems &&
      bookingItems.length
    ) {
      const cartItem = cartItems[0];
      let bookingItem;

      if (cartItem) {
        if (customerActivePackage) {
          bookingItem = isSingleBooking(cartItem)
            ? cartItem
            : getBookingItem(bookingItems);
        } else {
          bookingItem = getBookingItemByProductId(
            cartItem.productId,
            bookingItems
          );
        }
      }

      bookingItem = bookingItem || getBookingItem(bookingItems);

      setSelectedBookingOption(bookingItem);
      resetCartAndAddItemThenCreateSession([bookingItem]);
    }
  }, [customerCart, bookingItems]);

  useEffect(() => {
    if (
      isPaymentFetchingRef.current ||
      !customerCart ||
      !customerMembershipLoaded
    ) {
      return;
    }

    if (
      (isMembershipPurchase ||
        isAdditionalEventPurchase ||
        isSingleTicketPurchase ||
        isGatedEvent) &&
      membershipItems &&
      membershipItems.length
    ) {
      const item = getMembershipItemToPurchase(
        membershipItems,
        isMembershipPurchase,
        isSingleTicketPurchase || isGatedEvent
      );

      let gatedItem;

      if (customerMembership) {
        gatedItem = getGatedItem(membershipItems, confirmedBooking.tier);
        setSelectedPaymentOption(gatedItem);
      }

      setSelectedBookingOption(item);

      resetCartAndAddItemThenCreateSession([item, gatedItem]);
    }
  }, [
    customerCart,
    customerMembershipLoaded,
    membershipItems /*, isMembershipPurchase, isSingleTicketPurchase*/,
  ]);

  const handleAddressDetailsChange = useCallback((e) => {
    var value = e.target.value;

    setAddressDetails(value);
  }, []);

  const openInfoModal = useCallback((msg, title = "Sorry") => {
    return new Promise((resolve, reject) => {
      infoModalResolveRef.current = resolve;

      setIsInfoModalOpen(true);
      if (title === "Sorry") {
        setInfoModalStatus(":(");
      } else {
        setInfoModalStatus("Success");
      }
      setInfoModalTitle(title);
      setInfoModalMsg(msg);
    });
  }, []);

  const closeInfoModal = useCallback(() => {
    infoModalResolveRef.current();
    infoModalResolveRef.current = null;

    setIsInfoModalOpen(false);
  }, []);

  const openAdditionalNotesModal = useCallback(() => {
    setIsNotesModalOpen(true);
  }, []);

  const closeAdditionalNotesModal = useCallback(() => {
    setIsNotesModalOpen(false);
  }, []);

  const handleNotesChange = useCallback((event) => {
    const value = event.target.value;

    setAdditionalNotes(value);
  }, []);

  const openUserInfoModal = useCallback(() => {
    return new Promise((resolve, reject) => {
      userInfoModalResolveRef.current = resolve;
      userInfoModalRejectRef.current = reject;

      setIsUserInfoModalOpen(true);
    });
  }, []);

  const closeUserInfoModal = useCallback((name) => {
    if (
      !name ||
      !isValidFirstName(name.firstName) ||
      !isValidLastName(name.lastName)
    ) {
      userInfoModalRejectRef.current();
    } else {
      // setUserFirstName(name.firstName);
      // setUserFamilyName(name.familyName);

      updateCustomerInfo(name, user.id).finally(() => {
        userInfoModalResolveRef.current();
        userInfoModalResolveRef.current = null;
      });
    }

    userInfoModalRejectRef.current = null;
    setIsUserInfoModalOpen(false);
  }, []);

  useEffect(() => {
    if (!isMembershipPurchase && cartItems && cartItems.length) {
      const cartItem = cartItems[0];

      const bookingItem = getBookingItemByProductId(
        cartItem.productId,
        bookingItems //.concat(bookingPackageItems)
      );

      if (bookingItem) {
        setSelectedBookingOption(bookingItem);
      }
    }
  }, [cartItems, bookingItems /*, bookingPackageItems*/]);

  // Add item to cart
  const addItemToCart = useCallback(
    (bookingOption) => {
      if (!latestCustomerCart.current || !bookingOption) {
        return;
      }

      return addProductToCart(latestCustomerCart.current.id, {
        customerId: confirmedBooking.customerId,
        productId: bookingOption.productId,
        metadata: Object.assign(
          {
            customerId: confirmedBooking.customerId.toString(),
            productId: confirmedBooking.productId,
            start: confirmedBooking.start,
            note: additionalNotes,
            timezone: getTz(region), //"America/Los_Angeles", // TODO get from location
          },
          (isAdditionalEventPurchase ||
            isGatedEvent ||
            isSingleTicketPurchase) && {
            eventId: confirmedBooking.eventId,
          },
          !(
            isMembershipPurchase ||
            isAdditionalEventPurchase ||
            isGatedEvent ||
            isSingleTicketPurchase
          ) && {
            partnerId: confirmedBooking.partnerId.toString(),
          },
          confirmedBooking.location && {
            locationCode: confirmedBooking.location.code,
            locationGymName: "",
            locationLatitude: confirmedBooking.location.latitude,
            locationLongitude: confirmedBooking.location.longitude,
            locationAddress: confirmedBooking.location.address,
            locationDetail: addressDetails,
          },
          confirmedBooking.place && {
            /* can't do destructuring as "name" prop is unnecessary */
            locationCode: confirmedBooking.place.value,
            locationGymName: confirmedBooking.place.name,
            locationLatitude: confirmedBooking.place.latitude,
            locationLongitude: confirmedBooking.place.longitude,
            locationAddress: confirmedBooking.place.address,
            locationDetail: addressDetails,
          }
        ),
      });
    },
    [history, user, addressDetails, additionalNotes, confirmedBooking]
  );

  const addItem = useCallback(async (val, maxRetries) => {
    let tries = 0;

    for (; tries < maxRetries; tries++) {
      try {
        addProductToCartCancelablePromiseRef.current &&
          addProductToCartCancelablePromiseRef.current.cancel();

        const cancelablePromise = addItemToCart(val);

        addProductToCartCancelablePromiseRef.current = cancelablePromise; // assume addItemToCart returns a cancelable promise

        await cancelablePromise.promise;

        return; // success, exit the loop
      } catch (err) {
        if (err.isCanceled) {
          console.log(`Request canceled, retry #${tries + 1}/${maxRetries}`);
        } else {
          console.log(
            `Error adding item to the cart, retry #${
              tries + 1
            }/${maxRetries}: ${err}`
          );

          if (tries >= maxRetries - 1) {
            throw err; // re-throw the error if all retries failed
          }
        }

        // Wait 200ms before retrying
        await sleep(200);
      } finally {
        addProductToCartCancelablePromiseRef.current = null;
      }
    }

    // if all retries failed, return a rejected promise
    return Promise.reject(
      new Error(`All retries failed to add item to the cart`)
    );
  }, []);

  const removeItemsFromCart = useCallback(
    async (cartId, userId, maxRetries) => {
      let tries = 0;

      if (!cartId || !userId) {
        return;
      }

      for (; tries < maxRetries; tries++) {
        try {
          emptyCartCancelablePromiseRef.current &&
            emptyCartCancelablePromiseRef.current.cancel();

          const cancelablePromise = removeProductsFromCart(cartId, userId);

          emptyCartCancelablePromiseRef.current = cancelablePromise; // assume emptyCard returns a cancelable promise

          await cancelablePromise.promise;

          return; // success, exit the loop
        } catch (err) {
          if (err.isCanceled) {
            console.log(`Request canceled, retry #${tries + 1}/${maxRetries}`);
          } else {
            console.log(
              `Error emptying the cart, retry #${
                tries + 1
              }/${maxRetries}: ${err}`
            );

            if (tries >= maxRetries - 1) {
              throw err; // re-throw the error if all retries failed
            }
          }

          // Wait 1000ms before retrying
          await sleep(1000);
        } finally {
          emptyCartCancelablePromiseRef.current = null;
        }
      }

      // if all retries failed, return a rejected promise
      return Promise.reject(new Error(`All retries failed to empty the cart`));
    },
    []
  );

  const createSession = useCallback(
    (isPreviewSession) => {
      return Promise.resolve()
        .then(() => {
          return createCheckoutSession(
            Object.assign(
              {
                customerId: confirmedBooking.customerId,
              },
              promoCodeInputRef.current &&
                promoCodeInputRef.current.value &&
                !isReferralApplied && {
                  promocode: promoCodeInputRef.current.value
                    .trim()
                    .toUpperCase(),
                }
            ),
            isPreviewSession
          );
        })
        .then((res) => {
          window.fbq &&
            !isPreviewSession &&
            fbq("track", FB_EVENT_PURCHASE, {
              value: selectedBookingOption && selectedBookingOption.price,
              currency:
                (selectedBookingOption && selectedBookingOption.currency) ||
                "USD",
            });

          return res;
        })
        .catch((err) => {
          if (err && err.message !== "Invalid promocode!") {
            openInfoModal(err.message);
          }

          return Promise.reject(err);
        });
    },
    [confirmedBooking, selectedBookingOption, isReferralApplied]
  );

  function sleep(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  const handleSubmit = useCallback(async () => {
    if (selectedBookingOption === null && !customerActivePackage) {
      if (typeof Sentry !== "undefined") {
        Sentry.captureMessage(
          "Can't procced to pay, booking option is not selected.",
          {
            level: "debug", // Optional: specify the level of the message
            extra: {
              region,
            }, // Optional: include additional context
          }
        );
      }
      return;
    }

    if (!user || !customerCart) {
      if (typeof Sentry !== "undefined") {
        Sentry.captureMessage(
          "Can't procced to pay, user or customer cart is not set.",
          {
            level: "debug", // Optional: specify the level of the message
            extra: {
              region,
            }, // Optional: include additional context
          }
        );
      }
      return;
    }

    setIsPaymentFetching(true);

    Promise.resolve()
      .then(() => {
        if (!isMembershipPurchase) {
          if (typeof Sentry !== "undefined") {
            Sentry.captureMessage(
              "Checkout submit: not a membership purchase.",
              {
                level: "debug", // Optional: specify the level of the message
                extra: { region },
              }
            );
          }
          return;
        }

        // Customer has not set his name
        if (!isValidFirstName(firstName) || !isValidLastName(familyName)) {
          if (typeof Sentry !== "undefined") {
            Sentry.captureMessage("Checkout submit: customer name not set.", {
              level: "debug", // Optional: specify the level of the message
              extra: { region },
            });
          }
          return openUserInfoModal();
        } else if (
          isValidFirstName(firstName) &&
          isValidLastName(familyName) &&
          (user.firstName !== firstName || user.lastName !== familyName)
        ) {
          if (typeof Sentry !== "undefined") {
            Sentry.captureMessage("Checkout submit: updating customer info.", {
              level: "debug", // Optional: specify the level of the message
              extra: { region },
            });
          }
          return updateCustomerInfo(
            { firstName, lastName: familyName },
            user.id
          );
        }
      })
      .then(() => createSession())
      .then((res) => {
        if (typeof Sentry !== "undefined") {
          Sentry.captureMessage(
            "Checkout submit: redirecting to Stripe Checkout.",
            {
              level: "debug", // Optional: specify the level of the message
              extra: {
                stripe:
                  typeof stripe !== "undefined"
                    ? "stripe is set"
                    : "stripe is undefined!!",
                sessionId: res.data.id,
                region,
              }, // Optional: include additional context
            }
          );
        }

        stripe.redirectToCheckout({
          sessionId: res.data.id,
        });
      })
      .catch((err) => {
        if (typeof Sentry !== "undefined") {
          Sentry.captureMessage(
            "Checkout submit: there was an error redirecting to Stripe Checkout.",
            {
              level: "error", // Optional: specify the level of the message
              extra: { error: err, region }, // Optional: include additional context
            }
          );
        }
        console.error(err);
      })
      .finally(() => setIsPaymentFetching(false));
  }, [
    user,
    firstName,
    familyName,
    customerCart,
    isMembershipPurchase,
    selectedBookingOption,
    customerActivePackage,
  ]);

  const handlePromoCodeChange = useCallback(
    (value) => {
      setHasPromoCodeError(false);

      setPromoCode(value);

      setIsPromoCodeChecking(true);
      checkDiscountDebounced();

      if (
        value === "" ||
        (discountPromoCode && discountPromoCode.promoCode !== value)
      ) {
        clearDiscount();
      }
    },
    [discountPromoCode]
  );

  const handlePromoCodeClear = useCallback(() => {
    setHasPromoCodeError(false);
    setPromoCode("");

    clearDiscount();
  }, []);

  const referralBalance = referral
    ? referral.balance
    : referralValidation
    ? referralValidation.balance
    : 0;
  const referralCurrencySymbol = referral
    ? referral.symbol
    : referralValidation
    ? referralValidation.symbol
    : "$";

  let packageBalance;
  let sessionsLeft;

  if (customerActivePackage) {
    const packageSessions = Array.from({
      length: customerActivePackage.metadata.initialBalance,
    });
    sessionsLeft = customerActivePackage.metadata.balance;

    packageBalance = (
      <div
        className={`tbk-mt-2 tbk-flex tbk-justify-evenly tbk-mb-2${
          sessionsLeft ? "" : " no-sessions"
        }`}
      >
        {packageSessions.map((item, index) => (
          <div
            className={`tbk-mr-[2px] tbk-h-1 tbk-grow ${
              index === 0 ? "tbk-rounded-l-lg" : ""
            } ${
              index === packageSessions.length - 1 ? "tbk-rounded-r-lg" : ""
            } ${
              index < customerActivePackage.metadata.balance
                ? "tbk-bg-coral"
                : "tbk-bg-blue-grey-light"
            }`}
          />
        ))}
      </div>
    );
  }

  const bookingWithSingleSession =
    isSingleBooking(selectedBookingOption) && !customerActivePackage;

  let amountTotal = 0;
  let amountDiscount = 0;
  let promoCodeUpperCased = promoCode ? promoCode.trim().toUpperCase() : "";
  let promoApplied =
    discountPromoCode && discountPromoCode.promoCode === promoCodeUpperCased;

  if (previewCheckoutSession) {
    let price = (previewCheckoutSession.amountTotal / 100).toFixed(2);
    let discount = (
      previewCheckoutSession.totalDetails.amountDiscount / 100
    ).toFixed(2);

    // Remove decimal and trailing zeroes if no fractional value
    price = price.replace(/\.00$/, "");
    discount = discount.replace(/\.00$/, "");

    amountTotal = price;
    amountDiscount = promoApplied ? discount : 0;
  } else if (selectedBookingOption) {
    amountTotal = selectedBookingOption.price.amount;
  }

  const savingsValue =
    isTrialAvailable && isMembershipPurchase
      ? selectedBookingOption
        ? selectedBookingOption.price.amount
        : 0
      : isReferralApplied
      ? Math.min(
          referralBalance,
          selectedBookingOption ? selectedBookingOption.price.amount : 0
        )
      : customerActivePackage
      ? ""
      : amountDiscount
      ? amountDiscount
      : discountPromoCode
      ? discountPromoCode.discount
      : 0;

  const bookingOptions = confirmedBooking.eventId
    ? membershipItems.filter(
        (v) =>
          !v.metadata.isMembershipRequired &&
          v.metadata.code !== "GATED_EVENT" /* && v.metadata.type !== "EVENT"*/
      )
    : bookingItems; // bookingPackageItems.concat(bookingItems);

  const renderDiscount = () => {
    if (
      isTrialAvailable &&
      isMembershipPurchase &&
      !Number(amountDiscount) &&
      !(discountPromoCode && discountPromoCode.value)
    ) {
      return selectedBookingOption && !hasPromoCodeError
        ? `-${selectedBookingOption.price.symbol || "£"}${
            selectedBookingOption.price.amount
          }`
        : "-0";
    } else if (isReferralApplied) {
      return `-${referralCurrencySymbol}${Math.min(
        referralBalance,
        selectedBookingOption ? selectedBookingOption.price.amount : 0
      )}`;
    } else if (amountDiscount) {
      return `-${previewCheckoutSession.symbol}${amountDiscount}`;
    } else if (discountPromoCode && discountPromoCode.value) {
      return `-${discountPromoCode.symbol}${discountPromoCode.discount}`;
    } else if (customerActivePackage) {
      return `-$${price}`;
    } else {
      return "";
    }
  };

  const renderTotalPrice = () => {
    const currencySymbol = selectedBookingOption
      ? selectedBookingOption.price.symbol
      : user && user.region === "GB"
      ? "£"
      : "$";

    return isPaymentFetching ? (
      <PriceShimmer />
    ) : (
      `${currencySymbol}${customerActivePackage ? 0 : amountTotal}`
    );
  };

  const membershipItem = getMembershipItemToPurchase(
    membershipItems,
    isMembershipPurchase,
    isSingleTicketPurchase
  );

  var pageTitle = isMembershipPurchase
    ? `Purchase ${membershipItem ? membershipItem.name : ""}`
    : isAdditionalEventPurchase || isSingleTicketPurchase
    ? eventSummary
      ? eventSummary.name
      : ""
    : `Confirm your ${product ? product.name + " session" : " session"} with ${
        partnerSummary
          ? `${partnerSummary.firstName} ${partnerSummary.lastName}`
          : "TruBe"
      }`;

  let formattedDate;
  if (
    eventSummary &&
    (isAdditionalEventPurchase || isSingleTicketPurchase || isGatedEvent)
  ) {
    formattedDate = formatEventDates(eventSummary, region);
  }

  return (
    <>
      <ScrollToTopOnMount />
      <Title>{pageTitle}</Title>
      {/*<Meta></Meta>*/}
      <Header showRegionSelect={false} showLinks={false} showMenu={false} />
      <div className="tbk-grow tbk-bg-basic-white tbk-pb-24">
        <div className="tbk-container tbk-mx-auto tbk-pb-24 lg:tbk-pb-24">
          <div className="tbk-mb-4 tbk-mt-3 tbk-flex tbk-items-center tbk-px-2 tbk-text-primary lg:tbk-mt-5 lg:tbk-mb-6">
            <button className="tbk-mr-3" onClick={() => history.goBack()}>
              <ArrowLeftIcon />
            </button>
            <div className="tbk-text-bright md:tbk-text-epic-medium">
              Checkout
            </div>
          </div>
          <div className="tbk-mt-4 lg:tbk-mt-0 lg:tbk-grid lg:tbk-grid-cols-2 lg:tbk-gap-32 lg:tbk-px-2">
            <div className="tbk-mb-5_ tbk-mb-6 tbk-px-2 lg:tbk-px-0">
              <div className="tbk-mb-4 tbk-flex tbk-items-start tbk-border-b tbk-border-solid tbk-border-blue-grey-light tbk-pb-4 md:tbk-mb-3">
                {isMembershipPurchase ? (
                  <div className="tbk-relative tbk-mr-1 tbk-h-34 tbk-shrink-0 tbk-grow-0 tbk-basis-34 md:tbk-mr-2">
                    <LotusBig className={"tbk-mr-1"} />
                  </div>
                ) : isAdditionalEventPurchase ||
                  isSingleTicketPurchase ||
                  isGatedEvent ? (
                  <div className="tbk-relative tbk-mr-2 tbk-h-34 tbk-shrink-0 tbk-grow-0 tbk-basis-34 md:tbk-mr-2">
                    <img
                      className="tbk-w-full tbk-rounded-2xl tbk-object-cover"
                      src={eventSummary && eventSummary.pastPhoto}
                      alt={eventSummary && eventSummary.name}
                      // alt={trainer.name}
                    />
                  </div>
                ) : (
                  <div className="tb-profile-picture tbk-relative tbk-mr-1 tbk-h-34 tbk-shrink-0 tbk-grow-0 tbk-basis-34 md:tbk-mr-2">
                    <div className="tbk-absolute tbk-right-0 tbk-bottom-0 tbk-rounded-lg tbk-bg-basic-white">
                      <TrainerRating
                        className="tbk-flex tbk-items-center tbk-rounded-lg tbk-pr-1 tbk-shadow-trube"
                        averageRatingClassName="tbk-text-coral tbk-text-secondary"
                        fractionDigits={1}
                        partner={{
                          ...partnerSummary,
                          partnerRatingRecap: {
                            average: partnerSummary.rating,
                            numberOfRatings: 0,
                          },
                        }}
                      />
                    </div>
                    <img
                      className="tb-profile-picture-img img-responsive tbk-h-full tbk-w-full tbk-rounded-2xl tbk-object-cover"
                      itemProp="image"
                      src={
                        getPartnerPictureUrl(partnerSummary) ||
                        defaultPartnerPhoto
                      }
                      alt={`${partnerSummary.firstName} ${partnerSummary.lastName} photo`}
                    />
                  </div>
                )}

                {isMembershipPurchase ? (
                  <div className="tbk-flex_ tbk-items-center tbk-justify-between">
                    <div className="tbk-text-h3-subtitle tbk-text-primary">
                      {membershipItem ? membershipItem.name : ""}
                    </div>
                    <div className="tbk-text-main tbk-mb-1 tbk-text-blue-grey">
                      Next payment:{" "}
                      {getMomentWithTz(new Date(), region)
                        .add(1, "month")
                        .format("MM.DD.YYYY")}
                    </div>
                    <div className="tbk-text-main tbk-text-blue-grey">
                      Subscription
                    </div>
                  </div>
                ) : isAdditionalEventPurchase ||
                  isSingleTicketPurchase ||
                  isGatedEvent ? (
                  <div className="tbk-flex_ tbk-items-center tbk-justify-between">
                    <div className="tbk-text-h3-subtitle tbk-text-primary">
                      {eventSummary ? eventSummary.name : ""}
                    </div>
                    <div className="tbk-text-main tbk-mb-1 tbk-text-primary">
                      {formattedDate && formattedDate[0]}
                      {", "}
                      {formattedDate && formattedDate[1]}
                    </div>
                    <div className="tbk-text-main tbk-text-blue-grey">
                      {isAdditionalEventPurchase
                        ? "Single class"
                        : isGatedEvent
                        ? "Elevated Class"
                        : "Single Class Ticket"}
                    </div>
                  </div>
                ) : (
                  <div className="tbk-min-w-0">
                    <div className="tbk-text-small md:tbk-text-main tbk-mb-0.5 tbk-mr-1 tbk-overflow-hidden tbk-text-ellipsis tbk-whitespace-nowrap tbk-text-blue-grey">
                      60 minutes{" "}
                      <span className="tbk-text-coral">{"In-Person"}</span>{" "}
                      session with
                    </div>

                    <div className="tbk-text-title-medium md:tbk-text-bright tbk-mb-2 tbk-overflow-hidden tbk-text-ellipsis tbk-whitespace-nowrap tbk-text-primary">
                      {partnerSummary.firstName} {partnerSummary.lastName}
                    </div>
                    <div className="tbk-mb-2 md:tbk-mb-3 md:tbk-flex md:tbk-flex-wrap md:tbk-flex-nowrap md:tbk-items-center">
                      <div className="tbk-text-small md:tbk-text-main tbk-mr-3 tbk-mb-1 tbk-text-primary md:tbk-mb-0 md:tbk-text-blue-grey">
                        Discipline:
                      </div>
                      <PartnerProductBadge
                        className="tbk-inline-flex tbk-h-4 tbk-min-w-0 tbk-py-0.5 tbk-px-1"
                        product={product}
                      />
                    </div>
                  </div>
                )}
              </div>

              {!(
                isMembershipPurchase ||
                isAdditionalEventPurchase ||
                isGatedEvent ||
                isSingleTicketPurchase
              ) ? (
                <>
                  <div className="tbk-text-small tbk-mb-0.5 tbk-text-primary">
                    Date and time:
                  </div>
                  <div className="tbk-mb-1 tbk-flex tbk-items-center tbk-rounded-lg tbk-border tbk-border-blue-grey-light tbk-bg-grey-light tbk-py-1 tbk-px-2">
                    <div className="tbk-mr-1 tbk-shrink-0 tbk-text-green">
                      <CalendarTruBeIcon size={32} />
                    </div>
                    <div className="tbk-text-main tbk-overflow-hidden tbk-text-ellipsis tbk-whitespace-nowrap tbk-text-primary">
                      {getMomentWithTz(confirmedBooking.start, region).format(
                        clientWidth() < theme.responsive.breakpoints.sm
                          ? "h:mma ddd, MMMM DD, YYYY"
                          : "h:mma dddd, MMMM DD, YYYY"
                      )}
                    </div>
                  </div>
                  <div className="tbk-mb-2 tbk-flex md:tbk-mb-5">
                    <ExclamationMarkOIcon className="tbk-mr-1 tbk-text-blue-grey" />

                    <div className="tbk-text-small tbk-text-blue-grey">
                      Time set to{" "}
                      <TimeZoneLabel
                        region={
                          user
                            ? user.region
                            : region /*process.env.REACT_APP_REGION*/
                        }
                      />
                    </div>
                  </div>

                  <div className="tbk-text-small tbk-mb-0.5 tbk-text-primary">
                    Training location:
                  </div>
                  <div className="tbk-mb-2 tbk-flex tbk-items-center tbk-rounded-lg tbk-border tbk-border-blue-grey-light tbk-bg-grey-light tbk-py-1 tbk-px-2">
                    <div className="tbk-mr-1 tbk-shrink-0 tbk-text-green">
                      <MapMarkerIcon size={32} />
                    </div>
                    <div className="tbk-text-main tbk-overflow-hidden tbk-text-ellipsis tbk-whitespace-nowrap tbk-text-primary">
                      {confirmedBooking.location
                        ? confirmedBooking.location.address
                        : confirmedBooking.place
                        ? `${confirmedBooking.place.name || "Gym"} (${
                            confirmedBooking.place.address
                          })`
                        : ""}
                    </div>
                  </div>

                  <div className="tbk-text-small tbk-mb-0.5 tbk-text-primary">
                    Address details:
                  </div>
                  <input
                    className="tbk-input-field tbk-text-main tbk-mb-6 tbk-overflow-hidden tbk-text-ellipsis tbk-whitespace-nowrap tbk-text-primary md:tbk-mb-5"
                    placeholder="Entrance, apartment, floor, etc."
                    value={
                      confirmedBooking.place
                        ? confirmedBooking.place.detail
                        : addressDetails
                    }
                    readOnly={confirmedBooking.place}
                    onChange={handleAddressDetailsChange}
                  />
                </>
              ) : null}

              <div className="tbk-text-h3-subtitle tbk-mb-3 tbk-text-primary">
                Your Contacts:
              </div>
              {isMembershipPurchase && false ? (
                <UserContactsBox
                  userFirstName={isValidFirstName(firstName) ? firstName : ""}
                  userLastName={isValidLastName(familyName) ? familyName : ""}
                  userPhoneNumber={`${user.phoneCode} ${user.phoneNumber}`}
                  onFirstNameChange={setUserFirstName}
                  onLastNameChange={setUserFamilyName}
                />
              ) : (
                <>
                  <div className="tbk-mb-2 tbk-items-end tbk-rounded-2xl tbk-bg-grey-light tbk-p-2 md:tbk-flex">
                    <div className="tbk-flex tbk-grow">
                      <div className="tbk-mr-3 tbk-mb-3 tbk-inline-block">
                        <GenericUserProfilePhoto size={48} />
                      </div>

                      <div className="tbk-mb-1 tbk-min-w-0 md:tbk-mb-0">
                        <div className="tbk-text-title-medium tbk-mb-1 tbk-text-primary">
                          {user && user.firstName}
                        </div>
                        <div className="tbk-text-title-main tbk-text-primary">
                          {user && user.phoneCode}
                          {user && user.phoneNumber}
                        </div>
                        <div className="tbk-text-title-main tbk-overflow-hidden tbk-text-ellipsis tbk-text-primary">
                          {user && user.email}
                        </div>
                      </div>
                    </div>
                    <button className="tbk-invisible tbk-ml-7 tbk-flex tbk-items-center tbk-rounded-lg tbk-bg-basic-white tbk-py-0.5 tbk-px-1 tbk-text-coral md:tbk-ml-0">
                      <PenIcon />
                      <div className="tbk-text-small">Change</div>
                    </button>
                  </div>

                  <div className="tbk-mb-5 tbk-flex">
                    <p className="tbk-text-secondary tbk-text-blue-grey">
                      We use the contact information to provide the booking
                      details.
                      <br />
                      Please, make sure it's relevant.
                    </p>
                  </div>
                </>
              )}

              {!(
                isMembershipPurchase ||
                isAdditionalEventPurchase ||
                isSingleTicketPurchase
              ) ? (
                <>
                  <div className="tbk-text-small tbk-mb-0.5 tbk-text-primary">
                    Additional notes:
                  </div>
                  <div
                    className="tbk-flex tbk-cursor-pointer tbk-items-center tbk-justify-between tbk-rounded-lg tbk-border tbk-border-blue-grey-light tbk-bg-basic-white tbk-py-1 tbk-px-2"
                    onClick={openAdditionalNotesModal}
                  >
                    <div
                      className={`tbk-text-main ${
                        additionalNotes
                          ? "tbk-text-primary"
                          : "tbk-text-grey-inactive"
                      } tbk-overflow-hidden tbk-text-ellipsis tbk-whitespace-nowrap`}
                    >
                      {additionalNotes ||
                        "Add any additional information for your coach"}
                    </div>
                    <button className="tbk-text-small tbk-flex tbk-items-center tbk-py-0.5 tbk-px-0.5 tbk-text-coral">
                      <PenIcon />
                      Add
                    </button>
                  </div>
                </>
              ) : null}
            </div>

            {/*Right col*/}
            <div className="tbk-flex tbk-flex-col tbk-justify-between">
              <div className="md:tbk--mb-3">
                <div className="tbk-text-h3-subtitle tbk-mb-2 tbk-px-2 tbk-text-primary md:tbk-mb-3 lg:tbk-px-0">
                  {isMembershipPurchase ||
                  isAdditionalEventPurchase ||
                  isGatedEvent ||
                  isSingleTicketPurchase
                    ? "Cart Details:"
                    : "Session Package:"}
                </div>
                <div className="tbk-rounded-2xl tbk-bg-grey-light tbk-p-2">
                  {customerActivePackage ? (
                    <div className="tbk-mb-5">
                      <div className="tbk-mb-2 tbk-flex tbk-items-center tbk-rounded-lg tbk-bg-basic-white tbk-py-2 tbk-px-3">
                        <SessionTypeIcon className="tbk-mr-1 tbk-drop-shadow-trube" />
                        <div className="tbk-text-main tbk-text-primary">
                          Your Session Balance
                        </div>
                      </div>
                      {packageBalance}
                      <div className="tbk-text-main tbk-text-blue-grey">
                        {sessionsLeft} session{sessionsLeft > 1 ? "s" : ""} left
                      </div>
                    </div>
                  ) : isAdditionalEventPurchase ||
                    isGatedEvent ||
                    (isMembershipPurchase && !confirmedBooking.eventId) ? (
                    <div />
                  ) : (
                    <div className="tbk-mb-3 tbk-rounded-2xl tbk-border tbk-border-solid tbk-border-blue-grey-light tbk-bg-basic-white tbk-p-2">
                      {bookingOptions && bookingOptions.length
                        ? bookingOptions.map((subscription, index) => (
                            <>
                              {index > 0 ? <hr className="" /> : null}
                              <BookingOption
                                key={subscription.productId}
                                id={subscription.productId}
                                title={
                                  subscription.name === "Booking"
                                    ? "Single Session"
                                    : subscription.metadata.type ===
                                      "EVENT_MEMBERSHIP"
                                    ? "Membership (Monthly)"
                                    : subscription.metadata.type === "EVENT"
                                    ? "Single Class"
                                    : subscription.name
                                }
                                bsetSeller={
                                  subscription.metadata.sessions === 4 ||
                                  subscription.metadata.tag === "Best saving" ||
                                  subscription.metadata.bestSeller ? (
                                    <div className="best-seller-tag" />
                                  ) : null
                                }
                                price={subscription.price.amount}
                                currencySymbol={subscription.price.symbol}
                                featuredText={
                                  subscription.metadata.type ===
                                  "EVENT_MEMBERSHIP" ? (
                                    <>
                                      <div>
                                        <span className="tbk-text-green">
                                          3 classes
                                        </span>{" "}
                                        per month
                                      </div>
                                      {/*<div>3 classes per month thereafter</div>*/}
                                    </>
                                  ) : !subscription.metadata
                                      .isMembershipRequired &&
                                    subscription.metadata.type === "EVENT" ? (
                                    "Best way to try"
                                  ) : (
                                    subscription.description
                                  )
                                }
                                save={subscription.price.save}
                                discount={subscription.price.discount}
                                sessions={subscription.sessions}
                                value={subscription}
                                checked={
                                  (selectedBookingOption &&
                                    selectedBookingOption.productId) ===
                                  subscription.productId
                                }
                                onChange={handleBookingOptionChange}
                              />
                            </>
                          ))
                        : [...Array(isSingleTicketPurchase ? 2 : 4)].map(
                            (v, index) => (
                              <>
                                {index > 0 ? <hr className="" /> : null}
                                <BookingOptionPlaceholder />
                              </>
                            )
                          )}
                    </div>
                  )}

                  {!customerActivePackage &&
                    !(
                      isMembershipPurchase ||
                      isAdditionalEventPurchase ||
                      isGatedEvent ||
                      isSingleTicketPurchase
                    ) && (
                      <div
                        className={`tbk-text-small ${
                          selectedBookingOption &&
                          selectedBookingOption.quantity === "SINGLE"
                            ? "tbk-mb-3"
                            : "tbk-mb-4"
                        } tbk-flex tbk-justify-center tbk-text-primary`}
                      >
                        <ExclamationMarkOIcon className="tbk-mr-1" />
                        <div>The sessions in packages have no expiry date</div>
                      </div>
                    )}
                  {(bookingWithSingleSession && !isReferralApplied) ||
                  isMembershipPurchase ||
                  isAdditionalEventPurchase ||
                  isGatedEvent ||
                  isSingleTicketPurchase ? (
                    <PromoCodeInput
                      inputRef={promoCodeInputRef}
                      hasError={hasPromoCodeError}
                      applied={promoApplied}
                      promoCode={promoCode}
                      discountPromoCode={discountPromoCode}
                      disabled={
                        !selectedBookingOption ||
                        isPromoCodeChecking ||
                        isPaymentFetching ||
                        isFetching
                      }
                      isBusy={
                        isPromoCodeChecking || isPaymentFetching || isFetching
                      }
                      onChange={handlePromoCodeChange}
                      onApply={checkDiscount}
                      onClear={handlePromoCodeClear}
                    />
                  ) : null}
                </div>
              </div>

              <div className="tbk-flex tbk-grow tbk-items-end">
                <div
                  className={`tbk-relative${
                    ((isMembershipPurchase && !confirmedBooking.eventId) ||
                      isAdditionalEventPurchase) &&
                    clientWidth() >= theme.responsive.breakpoints.lg
                      ? ""
                      : " tbk-sticky"
                  } ${
                    isMembershipPurchase || isAdditionalEventPurchase
                      ? "tbk-bottom-0"
                      : "tbk-bottom-8"
                  } tbk-w-full tbk-rounded-2xl tbk-bg-basic-white tbk-p-2 tbk-shadow-trube md:tbk-p-3`}
                >
                  <div className="tbk-absolute tbk--top-0.5 tbk-left-1/2 tbk--translate-x-1/2">
                    <svg
                      width="296"
                      height="8"
                      viewBox="0 0 296 8"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <circle cx="4" cy="4" r="4" fill="white" />
                      <circle cx="68" cy="4" r="4" fill="white" />
                      <circle cx="132" cy="4" r="4" fill="white" />
                      <circle cx="180" cy="4" r="4" fill="white" />
                      <circle cx="260" cy="4" r="4" fill="white" />
                      <circle cx="36" cy="4" r="4" fill="white" />
                      <circle cx="100" cy="4" r="4" fill="white" />
                      <circle cx="164" cy="4" r="4" fill="white" />
                      <circle cx="228" cy="4" r="4" fill="white" />
                      <circle cx="292" cy="4" r="4" fill="white" />
                      <circle cx="20" cy="4" r="4" fill="white" />
                      <circle cx="84" cy="4" r="4" fill="white" />
                      <circle cx="148" cy="4" r="4" fill="white" />
                      <circle cx="212" cy="4" r="4" fill="white" />
                      <circle cx="276" cy="4" r="4" fill="white" />
                      <circle cx="52" cy="4" r="4" fill="white" />
                      <circle cx="116" cy="4" r="4" fill="white" />
                      <circle cx="196" cy="4" r="4" fill="white" />
                      <circle cx="244" cy="4" r="4" fill="white" />
                    </svg>
                  </div>

                  <div className="tbk-mb-2 tbk-flex tbk-justify-between">
                    <div className="tbk-text-main tbk-text-grey-main">
                      {customerActivePackage
                        ? "Regular price"
                        : isMembershipPurchase
                        ? "TruBe Lifestyle Collective"
                        : isAdditionalEventPurchase
                        ? "Additional Event"
                        : isGatedEvent
                        ? "Elevated Class"
                        : isSingleTicketPurchase
                        ? "Single Class"
                        : isSingleBooking(selectedBookingOption)
                        ? "Single session"
                        : selectedBookingOption && selectedBookingOption.name}
                      :
                    </div>
                    <div className="tbk-text-main tbk-text-grey-main">
                      {isGatedEvent
                        ? selectedPaymentOption
                          ? `${selectedPaymentOption.price.symbol || "£"}${
                              selectedPaymentOption.price.amount
                            }`
                          : ""
                        : selectedBookingOption
                        ? `${selectedBookingOption.price.symbol || "£"}${
                            selectedBookingOption.price.amount
                          }`
                        : ""}
                    </div>
                  </div>

                  <div
                    className={`tbk-mb-2 tbk-flex tbk-justify-between${
                      (selectedBookingOption &&
                        selectedBookingOption.quantity === "MULTIPLE") ||
                      !savingsValue
                        ? " tbk-hidden"
                        : ""
                    }`}
                  >
                    <div
                      className={`tbk-text-main ${
                        (isTrialAvailable && isMembershipPurchase) ||
                        customerActivePackage ||
                        isReferralApplied ||
                        amountDiscount ||
                        (discountPromoCode && discountPromoCode.value)
                          ? "tbk-text-coral"
                          : "tbk-text-grey-inactive"
                      }`}
                    >
                      {customerActivePackage
                        ? "Package saving"
                        : isReferralApplied
                        ? "Referral Credits"
                        : isTrialAvailable && isMembershipPurchase
                        ? "7-Day Free Trial"
                        : "Savings"}
                      :
                    </div>
                    <div
                      className={`tbk-text-main ${
                        customerActivePackage ||
                        (isTrialAvailable && isMembershipPurchase) ||
                        isReferralApplied ||
                        amountDiscount ||
                        (discountPromoCode && discountPromoCode.value)
                          ? "tbk-text-coral"
                          : "tbk-text-grey-inactive"
                      }`}
                    >
                      {renderDiscount()}
                    </div>
                  </div>

                  <hr className="" />

                  <div className="tbk-mb-2 tbk-flex tbk-justify-between">
                    <div className="tbk-text-title-bold tbk-text-primary">
                      Total:
                    </div>
                    <div className="tbk-text-title-bold tbk-text-primary">
                      {renderTotalPrice()}
                    </div>
                  </div>

                  <Button
                    // sessionId={checkoutSession && checkoutSession.id}
                    priceId={
                      selectedBookingOption &&
                      selectedBookingOption.price.priceId
                    }
                    className="tbk-w-full"
                    disabled={
                      isFetching ||
                      isPaymentFetching ||
                      // !checkoutSession ||
                      (selectedBookingOption === null && !customerActivePackage)
                    }
                    showLoader={isPaymentFetching || isFetching}
                    onClick={handleSubmit}
                  >
                    Proceed to Pay
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Footer />
      <Modal
        appElement={/*__CLIENT__ && */ document.getElementById("#app")}
        isOpen={isNotesModalOpen}
        onRequestClose={closeAdditionalNotesModal}
        style={defaultCustomStyle}
        contentLabel="Additional notes"
      >
        <div className="modal-notes tbk-relative tbk-mb-3 tbk-rounded-lg tbk-bg-basic-white tbk-p-3 md:tbk-p-0">
          <h4 className="tbk-text-title-bold tbk-mt-1 tbk-mb-3 tbk-text-primary">
            Additional notes
          </h4>
          <div className="modal-body">
            <div className="tbk-relative">
              <PenIcon className="tbk-pointer-events-none tbk-absolute tbk-top-1.5 tbk-left-1.5 tbk-z-10" />
              <textarea
                className="tbk-input-field tbk-w-full tbk-pt-1.5 tbk-pr-2 tbk-pl-5 tbk-pb-3 tbk-text-primary"
                name="notes"
                rows="5"
                placeholder="I would like to tone up and lose weight. With more focus on toning my lower body in this session."
                value={additionalNotes}
                onChange={handleNotesChange}
              />
            </div>
          </div>
          <div className="modal-footer tbk-mt-3">
            <Button className="" onClick={closeAdditionalNotesModal}>
              <span className="tbk-px-3">Apply</span>
            </Button>
          </div>
        </div>
      </Modal>
      <Modal
        appElement={/*__CLIENT__ && */ document.getElementById("#app")}
        isOpen={isInfoModalOpen}
        // onAfterOpen={afterOpenInfoModal}
        onRequestClose={closeInfoModal}
        style={
          clientWidth() < theme.responsive.breakpoints.sm
            ? modalStyles.infoMobile
            : modalStyles.info
        }
        contentLabel="Info Modal"
      >
        <div className="modal-info tbk-flex tbk-flex-col tbk-items-center">
          <button
            onClick={closeInfoModal}
            className="tbk-absolute tbk-top-2 tbk-right-2 tbk-z-10 tbk-hidden tbk-text-coral"
          >
            <CloseIcon />
          </button>
          <h3 className="tbk-text-title-bold tbk-mt-0 tbk-mb-2 tbk-text-center tbk-text-primary">
            {infoModalTitle}
          </h3>
          {infoModalStatus !== "Success" ? (
            <BlockerIcon size={48} className="tbk-mb-2" />
          ) : null}
          <p className="tbk-text-main tbk-mb-3 tbk-max-w-full tbk-text-center tbk-text-primary">
            {infoModalMsg}
          </p>
          {infoModalStatus === "Success" ? (
            <CheckNiceIcon
              size={64}
              className="tbk-mt-1 tbk-mb-4 tbk-text-green"
            />
          ) : null}
          <Button className="tbk-w-full" onClick={closeInfoModal}>
            Okay
          </Button>
        </div>
      </Modal>
      {isUserInfoModalOpen ? (
        <UserInfoModal
          isOpen={isUserInfoModalOpen}
          onClose={closeUserInfoModal}
        >
          <Registration ctaText={"Proceed"} onNext={closeUserInfoModal} />
        </UserInfoModal>
      ) : null}
    </>
  );
};

const getBookingItemByProductId = (prodId, bookingItems) =>
  bookingItems.find((val) => val.productId == prodId);

const isSingleBooking = (item) => item && item.metadata.type === "BOOKING";

const getBookingItem = (bookingItems) => bookingItems.find(isSingleBooking);

const getMembershipItemToPurchase = (
  membershipItems,
  isMembershipPurchase,
  isSingleTicketPurchase = false
) => {
  if (!membershipItems || !membershipItems.length) {
    return;
  }

  return membershipItems.find((val) =>
    isMembershipPurchase
      ? val.metadata.type === "EVENT_MEMBERSHIP"
      : isSingleTicketPurchase
      ? val.metadata.type === "EVENT" &&
        !val.metadata.isMembershipRequired &&
        !val.metadata.tier
      : val.metadata.type === "EVENT" &&
        val.metadata.isMembershipRequired &&
        !val.metadata.tier
  );
};

const getGatedItem = (membershipItems, tier) => {
  if (!membershipItems || !membershipItems.length) {
    return;
  }

  return membershipItems.find(
    (val) => val.metadata.type === "EVENT" && val.metadata.tier === tier
  );
};

export default Checkout;
